import { useEffect, useMemo, useRef, useState } from 'react';
import NavBar from 'components/nav-bar/NavBar';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { motion } from 'framer-motion';
import NotificationTab from '@/pages/shared/notification/index.jsx';
import ChatModel from '@/pages/b2b/loan-management/chat-model';
import ResponsiveLayout from '@/components/ResponsiveLayout';
import DesktopLayout from '@/components/ResponsiveLayout/DesktopLayout';
import MobileLayout from '@/components/ResponsiveLayout/MobileLayout';
import MobileNavBar from '@/components/MobileNavBar';
import MobileHeader from '@/components/MobileHeader';
import { useGetNotificationsQuery } from 'redux/services/notifications';
import PageTransition from '@/components/PageTransition';

import {
  FiHelpCircle,
  FiSearch,
  FiBell,
} from 'react-icons/fi';

import { scaleMotionProps } from 'constants/animationProps';
import CardLoader from 'wrapper/card-loader';
import { CheckAuth } from 'auth/auth';
import { company_name } from 'constants/constant';
import styles from './AppShell.module.css';

const parseBreadcrumbFromDocumentTitle = (titleText) => {
  if (!titleText) return [];

  const parts = titleText
    .split('|')
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length) return [];

  if (parts[parts.length - 1].toLowerCase() === String(company_name).toLowerCase()) {
    parts.pop();
  }

  return parts.filter(Boolean);
};

const getPathBreadcrumb = (pathname) => {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.includes('admin')) {
    if (segments.includes('employees')) return ['Admin', 'Employees'];
    if (segments.includes('branches')) return ['Admin', 'Branches'];
    if (segments.includes('consumers')) return ['Admin', 'Consumers'];
    if (segments.includes('invitations')) return ['Admin', 'Invitations'];
    return ['Admin'];
  }

  if (segments.includes('loanInformation')) {
    if (segments.includes('lead')) return ['Loan Information', 'Borrower Profile'];
    return ['Loan Information'];
  }

  if (segments.includes('pipeline')) {
    if (segments.includes('lead')) return ['Pipeline', 'Borrower Profile'];
    return ['Pipeline'];
  }

  if (segments.includes('reports')) return ['Reports'];
  if (segments.includes('tools')) return ['Tools'];
  if (segments.includes('quickPrice')) return ['Quick Price'];
  if (segments.includes('help-support')) return ['Help Support'];
  if (segments.includes('support-docs')) {
    const idx = segments.indexOf('support-docs');
    const topic = segments[idx + 1];
    const topicMap = {
      'getting-started': 'Getting Started',
      'release-notes': 'Release Notes',
      'user-guides': 'User Guides',
      'training-videos': 'Webinars & Training Videos',
    };
    if (topic && topicMap[topic]) return ['Support Docs', topicMap[topic]];
    return ['Support Docs'];
  }
  if (segments.includes('dashboard')) return ['Dashboard'];

  return ['Dashboard'];
};

const ShellTopBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isHelpOpen, setHelpOpen] = useState(false);
  const [isNotificationOpen, setNotificationOpen] = useState(false);
  const [showUnread, setShowUnread] = useState(false);
  const helpMenuRef = useRef(null);
  const notificationMenuRef = useRef(null);
  const [documentTitle, setDocumentTitle] = useState(
    typeof document !== 'undefined' ? document.title : ''
  );
  const {
    data: notificationData,
    isFetching,
    refetch,
  } = useGetNotificationsQuery(
    { showUnread },
    { refetchOnMountOrArgChange: 300 }
  );

  useEffect(() => {
    const handleClickAway = (event) => {
      if (
        helpMenuRef.current &&
        !helpMenuRef.current.contains(event.target)
      ) {
        setHelpOpen(false);
      }
      if (
        notificationMenuRef.current &&
        !notificationMenuRef.current.contains(event.target)
      ) {
        setNotificationOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, []);

  useEffect(() => {
    const handleOpenNotifications = () => {
      setNotificationOpen(true);
      setHelpOpen(false);
    };
    window.addEventListener('mira:open-notifications', handleOpenNotifications);
    return () => window.removeEventListener('mira:open-notifications', handleOpenNotifications);
  }, []);

  useEffect(() => {
    refetch();
  }, [location.pathname, refetch]);

  const handleNavigate = (path) => {
    setHelpOpen(false);
    navigate(path);
  };

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const titleElement = document.querySelector('title');
    if (!titleElement) return;

    const syncTitle = () => {
      setDocumentTitle(document.title || '');
    };

    syncTitle();
    const observer = new MutationObserver(syncTitle);
    observer.observe(titleElement, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  const pathBreadcrumb = useMemo(
    () => getPathBreadcrumb(location.pathname),
    [location.pathname]
  );

  const titleBreadcrumb = useMemo(
    () => parseBreadcrumbFromDocumentTitle(documentTitle),
    [documentTitle]
  );

  const currentBreadcrumb = useMemo(() => {
    if (!titleBreadcrumb.length) return pathBreadcrumb;
    if (!pathBreadcrumb.length) return titleBreadcrumb;

    if (
      titleBreadcrumb[0].toLowerCase() === pathBreadcrumb[0].toLowerCase()
    ) {
      return titleBreadcrumb;
    }

    return pathBreadcrumb;
  }, [pathBreadcrumb, titleBreadcrumb]);

  const trainingResource = import.meta.env.VITE_TRAINING_PORTAL_URL;
  const hasUnreadNotifications = notificationData?.data?.data?.some(
    (item) => item?.isRead === '0'
  );

  return (
    <>
      {isNotificationOpen && (
        <button
          type="button"
          className={styles.notificationBackdrop}
          aria-label="Close notifications overlay"
          onClick={() => setNotificationOpen(false)}
        />
      )}
      <header className={styles.topBar}>
      <div className={styles.topBarInner}>
        <div className={styles.topBarLeft}>
          <div className={styles.breadcrumbRow}>
            {currentBreadcrumb.map((crumb, index) => (
              <div key={`${crumb}-${index}`} className={styles.breadcrumbItem}>
                {index > 0 && (
                  <span className={styles.breadcrumbSeparator}>
                    &gt;
                  </span>
                )}
                <span
                  className={
                    index === currentBreadcrumb.length - 1
                      ? styles.breadcrumbActive
                      : styles.breadcrumbInactive
                  }
                >
                  {crumb}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Section - Actions */}
        <div className={styles.topBarRight}>
          {/* Search */}
          <button type="button" className={styles.topBarIconButton}>
            <FiSearch className={styles.topBarIcon} />
          </button>

          {/* Notifications */}
          <div className={styles.notificationMenu} ref={notificationMenuRef}>
            <button
              type="button"
              className={styles.topBarIconButton}
              onClick={() => {
                setNotificationOpen((prev) => !prev);
                setHelpOpen(false);
              }}
              aria-label="Notifications"
            >
              <FiBell className={styles.topBarIcon} />
              {hasUnreadNotifications && <span className={styles.notificationDot} />}
            </button>
            {isNotificationOpen && (
              <div className={styles.notificationDropdown}>
                <NotificationTab
                  notifications={notificationData?.data?.data}
                  isFetching={isFetching}
                  showUnread={showUnread}
                  setShowUnread={setShowUnread}
                  setNotification={setNotificationOpen}
                />
              </div>
            )}
          </div>

          {/* Support Menu */}
          <div className="relative" ref={helpMenuRef}>
            <button
              type="button"
              onClick={() => {
                setHelpOpen((state) => !state);
                setNotificationOpen(false);
              }}
              className={styles.supportButton}
            >
              <FiHelpCircle className={styles.topBarIcon} />
              <span>Support</span>
            </button>
            {isHelpOpen && (
              <div className={styles.supportDropdown}>
                <div className={styles.supportDropdownHeader}>
                  <p className={styles.supportDropdownTitle}>Help & Resources</p>
                </div>
                <div className={styles.supportDropdownBody}>
                  <button
                    type="button"
                    onClick={() => handleNavigate('/help-support')}
                    className={styles.supportDropdownItem}
                  >
                    <FiHelpCircle className={styles.supportDropdownItemIcon} />
                    <span>Help Center</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setHelpOpen(false);
                      if (trainingResource) {
                        window.open(trainingResource, '_blank', 'noopener');
                      }
                    }}
                    className={styles.supportDropdownItem}
                    disabled={!trainingResource}
                  >
                    <FiHelpCircle className={styles.supportDropdownItemIcon} />
                    <span>Training Videos</span>
                    {!trainingResource && (
                      <span className={styles.supportDropdownSoon}>Soon</span>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
      </header>
    </>
  );
};

//this is the second main layout component that wraps the entire web app

export const AppShell = ({
  logout: _logout,
  data,
}) => {
  const [mobileChatOpen, setMobileChatOpen] = useState(false);

  /* On mobile the chat is behind a toggle, so a screen handing something to
     the assistant would send it into a panel the user cannot see. Opening on
     the same event keeps the answer visible. On desktop the chat is docked, so
     this changes nothing there. */
  useEffect(() => {
    const handleChatHandoff = () => setMobileChatOpen(true);
    window.addEventListener('send-chat-message', handleChatHandoff);
    return () => window.removeEventListener('send-chat-message', handleChatHandoff);
  }, []);

  if (!data || !CheckAuth()) {
    return <CardLoader />;
  }

  return (
    <AnimatePresence>
      <motion.div {...scaleMotionProps}>
        <div className={styles.shellWrapper}>
          <ResponsiveLayout
            mobile={(
              <MobileLayout
                header={<MobileHeader />}
                content={
                  <>
                    <div className="p-3"><PageTransition key={location.pathname}><Outlet /></PageTransition></div>
                    {/* Mobile chat overlay */}
                    {mobileChatOpen && (
                      <div style={{
                        position: 'fixed', inset: 0, zIndex: 70,
                        background: '#fff', display: 'flex', flexDirection: 'column',
                      }}>
                        <ChatModel docked onClose={() => setMobileChatOpen(false)} />
                      </div>
                    )}
                  </>
                }
                mobileNav={<MobileNavBar onChatOpen={() => setMobileChatOpen(true)} />}
              />
            )}
            desktop={(
              <DesktopLayout
                sidebar={<NavBar />}
                topbar={(
                  <div className={styles.topBarPadding}>
                    <ShellTopBar />
                  </div>
                )}
                content={(
                  <div
                    className={`${styles.contentArea} ${styles.contentAreaWithChat}`}
                  >
                    <PageTransition key={location.pathname}>
                      <Outlet />
                    </PageTransition>
                  </div>
                )}
                aside={(
                  <aside className={styles.chatAside}>
                    <div className={styles.chatAsideInner}>
                      <ChatModel docked />
                    </div>
                  </aside>
                )}
              />
            )}
          />
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
